import { useEffect, useState } from 'react';
import {
    Save,
    AlertCircle,
    ArrowLeft,
    User,
    Phone,
    MapPin,
    Shield,
    FileText,
    Stethoscope
} from 'lucide-react';
import patientService from '../services/patientService';
import { API_URL } from '../config/api';
import './PatientFormV2.css';


// Regímenes fiscales SAT
const TAX_REGIMES = [
    { code: '601', name: 'General de Ley Personas Morales' },
    { code: '603', name: 'Personas Morales con Fines no Lucrativos' },
    { code: '605', name: 'Sueldos y Salarios e Ingresos Asimilados a Salarios' },
    { code: '606', name: 'Arrendamiento' },
    { code: '607', name: 'Régimen de Enajenación o Adquisición de Bienes' },
    { code: '608', name: 'Demás ingresos' },
    { code: '610', name: 'Residentes en el Extranjero sin Establecimiento Permanente en México' },
    { code: '611', name: 'Ingresos por Dividendos (socios y accionistas)' },
    { code: '612', name: 'Personas Físicas con Actividades Empresariales y Profesionales' },
    { code: '614', name: 'Ingresos por intereses' },
    { code: '615', name: 'Régimen de los ingresos por obtención de premios' },
    { code: '616', name: 'Sin obligaciones fiscales' },
    { code: '620', name: 'Sociedades Cooperativas de Producción que optan por diferir sus ingresos' },
    { code: '621', name: 'Incorporación Fiscal' },
    { code: '622', name: 'Actividades Agrícolas, Ganaderas, Silvícolas y Pesqueras' },
    { code: '623', name: 'Opcional para Grupos de Sociedades' },
    { code: '624', name: 'Coordinados' },
    { code: '625', name: 'Régimen de las Actividades Empresariales con ingresos a través de Plataformas Tecnológicas' },
    { code: '626', name: 'Régimen Simplificado de Confianza' }
];

// Usos de CFDI
const CFDI_USES = [
    { code: 'G01', name: 'Adquisición de mercancías' },
    { code: 'G02', name: 'Devoluciones, descuentos o bonificaciones' },
    { code: 'G03', name: 'Gastos en general' },
    { code: 'D01', name: 'Honorarios médicos, dentales y gastos hospitalarios' },
    { code: 'D02', name: 'Gastos médicos por incapacidad o discapacidad' },
    { code: 'D03', name: 'Gastos funerales' },
    { code: 'D04', name: 'Donativos' },
    { code: 'D05', name: 'Intereses reales efectivamente pagados por créditos hipotecarios (casa habitación)' },
    { code: 'D06', name: 'Aportaciones voluntarias al SAR' },
    { code: 'D07', name: 'Primas por seguros de gastos médicos' },
    { code: 'D08', name: 'Gastos de transportación escolar obligatoria' },
    { code: 'D09', name: 'Depósitos en cuentas para el ahorro, primas que tengan como base planes de pensiones' },
    { code: 'D10', name: 'Pagos por servicios educativos (colegiaturas)' },
    { code: 'P01', name: 'Por definir' },
    { code: 'S01', name: 'Sin efectos fiscales' },
    { code: 'CP01', name: 'Pagos' },
    { code: 'CN01', name: 'Nómina' }
];

// Límites de caracteres según la base de datos
const FIELD_LIMITS = {
    first_name: 255,
    last_name: 255,
    maternal_last_name: 255,
    phone: 20,
    emergency_contact_phone: 20,
    email: 255,
    address_street: 255,
    address_number: 50,
    address_colony: 255,
    address_city: 255,
    address_state: 255,
    address_zip: 10,
    insurance_provider: 255,
    policy_number: 255,
    emergency_contact_name: 255,
    referring_doctor_name: 255,
    referring_doctor_specialty: 100,
    preferred_doctor: 255,
    fiscal_name: 255,
    fiscal_rfc: 13,
    fiscal_zip: 5,
};

// Campos obligatorios (marcados con * en el formulario)
const REQUIRED_FIELDS = ['first_name', 'last_name', 'date_of_birth', 'gender'];
const PatientForm = ({ onCancel, onSuccess, patientId }) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [fieldErrors, setFieldErrors] = useState({});
    const [doctors, setDoctors] = useState([]);
    const [insurers, setInsurers] = useState([]);

    // Fetch doctors for dropdown
    useEffect(() => {
        const fetchDoctors = async () => {
            try {
                const res = await fetch(`${API_URL}/staff-doctors`, {
                    headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                });
                if (res.ok) {
                    const data = await res.json();
                    setDoctors(data);
                }
            } catch (error) {
                console.error('Error fetching doctors:', error);
            }
        };

        const fetchInsurers = async () => {
            try {
                const res = await fetch(`${API_URL}/insurers`, {
                    headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                });
                if (res.ok) {
                    const data = await res.json();
                    setInsurers(data.filter(i => i.is_active));
                }
            } catch (error) {
                console.error('Error fetching insurers:', error);
            }
        };

        fetchDoctors();
        fetchInsurers();
    }, []);

    // Helper para formatear fechas evitando problemas de zona horaria (T-1)
    // Extrae solo la parte YYYY-MM-DD del string ISO o datetime
    const formatDateForInput = (dateString) => {
        if (!dateString) return '';
        return String(dateString).substring(0, 10);
    };

    // Helper para generar iniciales del doctor (igual que el backend)
    const getInitials = (name) => {
        if (!name || typeof name !== 'string') return 'PAC';
        let cleanName = name.trim();
        const titles = ['Dr.', 'Dra.', 'Doctor ', 'Doctora ', 'Lic.', 'Ing.'];
        titles.forEach(title => {
            const regex = new RegExp(`^${title.replace('.', '\\.')}\\s*`, 'i');
            cleanName = cleanName.replace(regex, '');
        });
        cleanName = cleanName.trim();
        if (!cleanName) return 'PAC';
        const words = cleanName.split(/\s+/);
        let initials = '';
        for (const word of words) {
            if (word.length > 0) initials += word.charAt(0).toUpperCase();
        }
        return initials.substring(0, 4) || 'PAC';
    };

    const [formData, setFormData] = useState({
        first_name: '', last_name: '', maternal_last_name: '', date_of_birth: '',
        gender: 'masculino', phone: '', email: '',
        patient_code: '',
        patient_code_preview: '',
        expediente_doctor: '',
        address_street: '', address_number: '', address_colony: '', address_city: '',
        address_state: '', address_zip: '', is_insured: false, insurance_provider: '',
        policy_number: '', emergency_contact_name: '', emergency_contact_phone: '',
        referring_doctor_name: '', referring_doctor_specialty: '', consultation_reason: '',
        preferred_doctor: '',
        // Datos fiscales
        fiscal_name: '', fiscal_rfc: '', fiscal_zip: '', fiscal_regime: '', fiscal_cfdi_use: '', fiscal_direccion: '',
        notes: '',
        is_external_study: false,
        external_lab_target: ''
    });

    useEffect(() => {
        if (patientId) {
            const loadPatient = async () => {
                setLoading(true);
                try {
                    const data = await patientService.getById(patientId);
                    // Ensure we map data correctly and handle potential nulls
                    setFormData({
                        first_name: data.first_name || '',
                        last_name: data.last_name || '',
                        maternal_last_name: data.maternal_last_name || '',
                        date_of_birth: formatDateForInput(data.date_of_birth),
                        gender: data.gender || 'masculino',
                        phone: data.phone || '',
                        email: data.email || '',
                        patient_code: data.patient_code || '',
                        patient_code_preview: '',
                        expediente_doctor: '',
                        address_street: data.address_street || '',
                        address_number: data.address_number || '',
                        address_colony: data.address_colony || '',
                        address_city: data.address_city || '',
                        address_state: data.address_state || '',
                        address_zip: data.address_zip || '',
                        is_insured: Boolean(data.is_insured),
                        insurance_provider: data.insurance_provider || '',
                        policy_number: data.policy_number || '',
                        emergency_contact_name: data.emergency_contact_name || '',
                        emergency_contact_phone: data.emergency_contact_phone || '',
                        referring_doctor_name: data.referring_doctor_name || '',
                        referring_doctor_specialty: data.referring_doctor_specialty || '',
                        consultation_reason: data.consultation_reason || '',
                        preferred_doctor: data.preferred_doctor || '',
                        // Datos fiscales - map DB column names to frontend field names
                        fiscal_name: data.fiscal_name || data.fiscal_razon_social || '',
                        fiscal_rfc: data.fiscal_rfc || '',
                        fiscal_zip: data.fiscal_zip || '',
                        fiscal_regime: data.fiscal_regime || '',
                        fiscal_cfdi_use: data.fiscal_cfdi_use || data.fiscal_uso_cfdi || '',
                        fiscal_direccion: data.fiscal_direccion || '',
                        notes: data.notes || '',
                        // Detectar si es estudio externo por el código
                        is_external_study: data.patient_code?.startsWith('GEN-') || data.patient_code?.startsWith('CEN-') || data.patient_code?.startsWith('EXT-'),
                        external_lab_target: (data.patient_code?.startsWith('GEN-') || data.patient_code?.startsWith('CEN-')) ? 'CENTOGENE' : (data.patient_code?.startsWith('EXT-') ? 'ESTUDIOS' : '')
                    });
                } catch (err) {
                    console.error('Error loading patient:', err);
                    setError('Error al cargar datos del paciente.');
                } finally {
                    setLoading(false);
                }
            };
            loadPatient();
        }
    }, [patientId]);

    // Función para validar un campo individual
    const validateField = (name, value) => {
        let fieldError = '';

        // Validar campos requeridos
        if (REQUIRED_FIELDS.includes(name) && (!value || !String(value).trim())) {
            fieldError = 'Este campo es requerido';
        }
        // Validar longitud máxima
        else if (FIELD_LIMITS[name] && value && String(value).length > FIELD_LIMITS[name]) {
            fieldError = `Máximo ${FIELD_LIMITS[name]} caracteres permitidos`;
        }
        // Validar formato de email
        else if (name === 'email' && value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value))) {
            fieldError = 'Ingrese un correo electrónico válido';
        }

        return fieldError;
    };

    const updatePatientCodePreview = async (doctorName) => {
        if (!doctorName) return;
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_URL}/patients-next-code?doctor_name=${encodeURIComponent(doctorName)}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                setFormData(prev => ({ ...prev, patient_code_preview: data.code }));
            }
        } catch (err) {
            console.error('Error fetching next code:', err);
        }
    };

    // Función para validar todo el formulario
    const validateForm = () => {
        const newErrors = {};
        let isValid = true;

        Object.keys(formData).forEach(key => {
            const fieldError = validateField(key, formData[key]);
            if (fieldError) {
                newErrors[key] = fieldError;
                isValid = false;
            }
        });

        // Validar expediente_doctor en creación
        if (!patientId && !formData.is_external_study && (!formData.expediente_doctor || !formData.expediente_doctor.trim())) {
            newErrors.expediente_doctor = 'Debe seleccionar un médico para el expediente';
            isValid = false;
        }

        setFieldErrors(newErrors);
        return isValid;
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        const newValue = type === 'checkbox' ? checked : value;

        setFormData(prev => ({
            ...prev,
            [name]: newValue
        }));

        // Validar el campo en tiempo real
        const fieldError = validateField(name, newValue);
        setFieldErrors(prev => ({
            ...prev,
            [name]: fieldError
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (loading) return;

        // Validar formulario antes de guardar
        if (!validateForm()) {
            setError('Por favor corrija los errores antes de guardar');
            return;
        }

        setLoading(true);
        setError(null);
        try {
            // FIX: Estrategia de Mediodía (Noon Strategy)
            // Agregamos 12:00:00 a la fecha para evitar que cambios de zona horaria (UTC vs Local)
            // desplacen el día (T-1 o T+1). De esta forma tenemos un margen de +/- 12 horas.
            const dataToSend = { ...formData };
            if (dataToSend.date_of_birth && dataToSend.date_of_birth.length <= 10) {
                dataToSend.date_of_birth = `${dataToSend.date_of_birth} 12:00:00`;
            }

            if (patientId) {
                await patientService.update(patientId, dataToSend);
            } else {
                await patientService.create(dataToSend);
            }
            if (onSuccess) onSuccess();
        } catch (err) {
            console.error(err);
            const serverMessage = err.response?.data?.error || err.response?.data?.message;
            setError(serverMessage || 'Error al guardar el paciente.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="patient-form-wrapper">
            <div className="patient-card">
                {/* Header Fijo */}
                <div className="patient-card-header">
                    <button onClick={onCancel} className="btn-back" title="Volver">
                        <ArrowLeft size={20} />
                    </button>
                    <div className="header-content">
                        <h2>{patientId ? 'Editar Paciente' : 'Nuevo Paciente'}</h2>
                        <p>{patientId ? 'Modifique la información y guarde los cambios' : 'Complete la información del expediente clínico'}</p>
                    </div>
                </div>

                {/* Form Body - Scrollable */}
                <form onSubmit={handleSubmit} className="form-body-scroll">
                    {/* Selector de Tipo de Expediente (Top) - Solo mostrar en creación */}
                    {!patientId && (
                        <div className="patient-form-section no-bg no-border mb-4">
                            <div className="type-selector-pills main-toggle">
                                <button
                                    type="button"
                                    className={`pill-btn ${!formData.is_external_study ? 'active' : ''}`}
                                    onClick={() => setFormData(prev => ({ ...prev, is_external_study: false, expediente_doctor: '', external_lab_target: '' }))}
                                >
                                    <User size={18} /> Expediente Normal (Clínica)
                                </button>
                                <button
                                    type="button"
                                    className={`pill-btn ${formData.is_external_study ? 'active' : ''}`}
                                    onClick={() => {
                                        setFormData(prev => ({ ...prev, is_external_study: true, external_lab_target: 'CENTOGENE', expediente_doctor: 'CENTOGENE' }));
                                        updatePatientCodePreview('CENTOGENE');
                                    }}
                                >
                                    <FileText size={18} /> Solo Estudio (Externo)
                                </button>
                            </div>

                            {formData.is_external_study && (
                                <div className="type-selector-pills secondary mt-3">
                                    <button
                                        type="button"
                                        className="pill-btn active"
                                        style={{ cursor: 'default' }}
                                    >
                                        Centogene (GEN-)
                                    </button>
                                </div>
                            )}
                        </div>
                    )}

                    {error && (
                        <div className="error-message mb-6">
                            <AlertCircle size={20} />
                            <span>{error}</span>
                        </div>
                    )}

                    {/* Sección 1: Datos Personales */}
                    <div className="patient-form-section">
                        <div className="section-header">
                            <div className="section-icon"><User size={18} /></div>
                            <h3>Datos Personales</h3>
                        </div>
                        <div className="form-grid-layout cols-3">
                            <div className="input-group">
                                <label className="input-label">Nombre(s) *</label>
                                <input className={`input-field ${fieldErrors.first_name ? 'has-error' : ''}`} name="first_name" value={formData.first_name} onChange={handleChange} required />
                                {fieldErrors.first_name && <span className="field-error">{fieldErrors.first_name}</span>}
                            </div>
                            <div className="input-group">
                                <label className="input-label">Apellido Paterno *</label>
                                <input className={`input-field ${fieldErrors.last_name ? 'has-error' : ''}`} name="last_name" value={formData.last_name} onChange={handleChange} required />
                                {fieldErrors.last_name && <span className="field-error">{fieldErrors.last_name}</span>}
                            </div>
                            <div className="input-group">
                                <label className="input-label">Apellido Materno</label>
                                <input className="input-field" name="maternal_last_name" value={formData.maternal_last_name} onChange={handleChange} />
                            </div>
                            <div className="input-group">
                                <label className="input-label">Fecha Nacimiento *</label>
                                <input type="date" className={`input-field ${fieldErrors.date_of_birth ? 'has-error' : ''}`} name="date_of_birth" value={formData.date_of_birth} onChange={handleChange} required />
                                {fieldErrors.date_of_birth && <span className="field-error">{fieldErrors.date_of_birth}</span>}
                            </div>
                            <div className="input-group">
                                <label className="input-label">Género *</label>
                                <select className="input-field" name="gender" value={formData.gender} onChange={handleChange}>
                                    <option value="masculino">Masculino</option>
                                    <option value="femenino">Femenino</option>
                                </select>
                            </div>

                        </div>
                    </div>

                    <hr className="section-divider" />

                    {/* Sección 2: Contacto */}
                    <div className="patient-form-section">
                        <div className="section-header">
                            <div className="section-icon"><Phone size={18} /></div>
                            <h3>Contacto</h3>
                        </div>
                        <div className="form-grid-layout">
                            <div className="input-group">
                                <label className="input-label">Teléfono</label>
                                <input type="tel" className={`input-field ${fieldErrors.phone ? 'has-error' : ''}`} name="phone" value={formData.phone} onChange={handleChange} maxLength={20} />
                                {fieldErrors.phone && <span className="field-error">{fieldErrors.phone}</span>}
                            </div>
                            <div className="input-group">
                                <label className="input-label">Email</label>
                                <input type="email" className={`input-field ${fieldErrors.email ? 'has-error' : ''}`} name="email" value={formData.email} onChange={handleChange} />
                                {fieldErrors.email && <span className="field-error">{fieldErrors.email}</span>}
                            </div>
                        </div>
                    </div>

                    <hr className="section-divider" />

                    {/* Sección 3: Dirección */}
                    <div className="patient-form-section">
                        <div className="section-header">
                            <div className="section-icon"><MapPin size={18} /></div>
                            <h3>Dirección</h3>
                        </div>
                        <div className="form-grid-layout">
                            <div className="input-group span-full">
                                <label className="input-label">Calle</label>
                                <input className={`input-field ${fieldErrors.address_street ? 'has-error' : ''}`} name="address_street" value={formData.address_street} onChange={handleChange} maxLength={255} />
                                {fieldErrors.address_street && <span className="field-error">{fieldErrors.address_street}</span>}
                            </div>
                            <div className="input-group">
                                <label className="input-label">Número</label>
                                <input className={`input-field ${fieldErrors.address_number ? 'has-error' : ''}`} name="address_number" value={formData.address_number} onChange={handleChange} maxLength={50} />
                                {fieldErrors.address_number && <span className="field-error">{fieldErrors.address_number}</span>}
                            </div>
                            <div className="input-group">
                                <label className="input-label">Colonia</label>
                                <input className={`input-field ${fieldErrors.address_colony ? 'has-error' : ''}`} name="address_colony" value={formData.address_colony} onChange={handleChange} maxLength={255} />
                                {fieldErrors.address_colony && <span className="field-error">{fieldErrors.address_colony}</span>}
                            </div>
                            <div className="input-group">
                                <label className="input-label">Ciudad</label>
                                <input className={`input-field ${fieldErrors.address_city ? 'has-error' : ''}`} name="address_city" value={formData.address_city} onChange={handleChange} maxLength={255} />
                                {fieldErrors.address_city && <span className="field-error">{fieldErrors.address_city}</span>}
                            </div>
                            <div className="input-group">
                                <label className="input-label">Estado</label>
                                <input className={`input-field ${fieldErrors.address_state ? 'has-error' : ''}`} name="address_state" value={formData.address_state} onChange={handleChange} maxLength={255} />
                                {fieldErrors.address_state && <span className="field-error">{fieldErrors.address_state}</span>}
                            </div>
                            <div className="input-group">
                                <label className="input-label">Código Postal</label>
                                <input className={`input-field ${fieldErrors.address_zip ? 'has-error' : ''}`} name="address_zip" value={formData.address_zip} onChange={handleChange} maxLength={10} />
                                {fieldErrors.address_zip && <span className="field-error">{fieldErrors.address_zip}</span>}
                            </div>
                        </div>
                    </div>

                    <hr className="section-divider" />

                    {/* Sección 4: Seguro y Otros */}
                    <div className="patient-form-section">
                        <div className="section-header">
                            <div className="section-icon"><Shield size={18} /></div>
                            <h3>Seguro y Otros</h3>
                        </div>
                        <div className="form-grid-layout">
                            {!formData.is_external_study && (
                                <div className="input-group span-full">
                                    <label className="input-label">Identificación de Expediente *</label>
                                    {patientId ? (
                                        <input
                                            className="input-field input-locked"
                                            value={formData.patient_code}
                                            readOnly
                                            style={{ backgroundColor: '#f1f5f9', cursor: 'not-allowed', fontWeight: '600', color: '#083677' }}
                                        />
                                    ) : (
                                        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                                            <select
                                                className={`input-field ${fieldErrors.expediente_doctor ? 'has-error' : ''}`}
                                                name="expediente_doctor"
                                                value={formData.expediente_doctor || ''}
                                                onChange={async (e) => {
                                                    const doctorName = e.target.value;
                                                    setFormData(prev => ({ ...prev, expediente_doctor: doctorName, patient_code_preview: '' }));
                                                    if (doctorName) {
                                                        setFieldErrors(prev => ({ ...prev, expediente_doctor: '' }));
                                                        try {
                                                            const token = localStorage.getItem('token');
                                                            const res = await fetch(`${API_URL}/patients-next-code?doctor_name=${encodeURIComponent(doctorName)}`, {
                                                                headers: { 'Authorization': `Bearer ${token}` }
                                                            });
                                                            if (res.ok) {
                                                                const data = await res.json();
                                                                setFormData(prev => ({ ...prev, patient_code_preview: data.code }));
                                                            }
                                                        } catch (err) {
                                                            console.error('Error fetching next code:', err);
                                                        }
                                                    }
                                                }}
                                                style={{ flex: '1' }}
                                            >
                                                <option value="">Seleccione médico...</option>
                                                {doctors.map(doctor => (
                                                    <option key={doctor.id} value={doctor.name}>{doctor.name}</option>
                                                ))}
                                            </select>
                                            <input
                                                className="input-field"
                                                value={formData.patient_code_preview || ''}
                                                readOnly
                                                style={{ width: '120px', backgroundColor: '#f1f5f9', fontWeight: '700', color: '#083677', textAlign: 'center', fontSize: '1rem', flexShrink: 0 }}
                                                placeholder="---"
                                            />
                                        </div>
                                    )}
                                </div>
                            )}
                            <div className="input-group span-full">
                                <label className="checkbox-card">
                                    <input type="checkbox" name="is_insured" checked={formData.is_insured} onChange={handleChange} />
                                    <span>¿El paciente cuenta con Seguro Médico?</span>
                                </label>
                            </div>
                            {formData.is_insured && (
                                <>
                                    <div className="input-group">
                                        <label className="input-label">Seguro de Gastos Médicos</label>
                                        <select className="input-field" name="insurance_provider" value={formData.insurance_provider} onChange={handleChange}>
                                            <option value="">Seleccione un seguro...</option>
                                            {insurers.map(insurer => (
                                                <option key={insurer.id} value={insurer.name}>{insurer.name}</option>
                                            ))}
                                            <option value="Otro">Otro</option>
                                        </select>
                                    </div>
                                    <div className="input-group">
                                        <label className="input-label">Número de Póliza</label>
                                        <input className={`input-field ${fieldErrors.policy_number ? 'has-error' : ''}`} name="policy_number" value={formData.policy_number} onChange={handleChange} maxLength={255} />
                                        {fieldErrors.policy_number && <span className="field-error">{fieldErrors.policy_number}</span>}
                                    </div>
                                </>
                            )}
                            <div className="input-group">
                                <label className="input-label">Médico Preferente</label>
                                <select className={`input-field ${fieldErrors.preferred_doctor ? 'has-error' : ''}`} name="preferred_doctor" value={formData.preferred_doctor} onChange={handleChange}>
                                    <option value="">Seleccione un médico...</option>
                                    {doctors.map(doctor => (
                                        <option key={doctor.id} value={doctor.name}>{doctor.name}</option>
                                    ))}
                                </select>
                                {fieldErrors.preferred_doctor && <span className="field-error">{fieldErrors.preferred_doctor}</span>}
                            </div>
                            <div className="input-group-row">
                                <div className="input-group">
                                    <label className="input-label">Médico que lo Refiere</label>
                                    <input className={`input-field ${fieldErrors.referring_doctor_name ? 'has-error' : ''}`} name="referring_doctor_name" value={formData.referring_doctor_name} onChange={handleChange} placeholder="Nombre del médico" maxLength={255} />
                                    {fieldErrors.referring_doctor_name && <span className="field-error">{fieldErrors.referring_doctor_name}</span>}
                                </div>
                                <div className="input-group">
                                    <label className="input-label">Especialidad</label>
                                    <input className={`input-field ${fieldErrors.referring_doctor_specialty ? 'has-error' : ''}`} name="referring_doctor_specialty" value={formData.referring_doctor_specialty} onChange={handleChange} placeholder="Ej: Ginecología" maxLength={100} />
                                    {fieldErrors.referring_doctor_specialty && <span className="field-error">{fieldErrors.referring_doctor_specialty}</span>}
                                </div>
                            </div>
                            <div className="input-group span-full">
                                <label className="input-label">Motivo de Consulta</label>
                                <textarea
                                    className="input-field textarea-field"
                                    name="consultation_reason"
                                    value={formData.consultation_reason}
                                    onChange={handleChange}
                                    rows="2"
                                    placeholder="Describa brevemente el motivo de la consulta..."
                                ></textarea>
                            </div>
                            <div className="input-group span-full">
                                <label className="input-label">Notas Adicionales</label>
                                <textarea
                                    className="input-field textarea-field"
                                    name="notes"
                                    value={formData.notes}
                                    onChange={handleChange}
                                    rows="3"
                                    placeholder="Alergias, condiciones previas, etc."
                                ></textarea>
                            </div>
                        </div>
                    </div>

                    <hr className="section-divider" />

                    {/* Sección 5: Datos Fiscales */}
                    <div className="patient-form-section">
                        <div className="section-header">
                            <div className="section-icon"><FileText size={18} /></div>
                            <h3>Datos Fiscales (para Facturación)</h3>
                        </div>
                        <div className="form-grid-layout">
                            <div className="input-group span-full">
                                <label className="input-label">Razón Social / Nombre Fiscal</label>
                                <input className={`input-field ${fieldErrors.fiscal_name ? 'has-error' : ''}`} name="fiscal_name" value={formData.fiscal_name} onChange={handleChange} placeholder="Nombre o razón social como aparece en el RFC" maxLength={255} />
                                {fieldErrors.fiscal_name && <span className="field-error">{fieldErrors.fiscal_name}</span>}
                            </div>
                            <div className="input-group">
                                <label className="input-label">RFC</label>
                                <input className={`input-field ${fieldErrors.fiscal_rfc ? 'has-error' : ''}`} name="fiscal_rfc" value={formData.fiscal_rfc} onChange={handleChange} placeholder="RFC a 12 o 13 caracteres" maxLength={13} style={{ textTransform: 'uppercase' }} />
                                {fieldErrors.fiscal_rfc && <span className="field-error">{fieldErrors.fiscal_rfc}</span>}
                            </div>
                            <div className="input-group">
                                <label className="input-label">Código Postal Fiscal</label>
                                <input className={`input-field ${fieldErrors.fiscal_zip ? 'has-error' : ''}`} name="fiscal_zip" value={formData.fiscal_zip} onChange={handleChange} placeholder="C.P. del domicilio fiscal" maxLength={5} />
                                {fieldErrors.fiscal_zip && <span className="field-error">{fieldErrors.fiscal_zip}</span>}
                            </div>
                            <div className="input-group span-full">
                                <label className="input-label">Régimen Fiscal</label>
                                <select className="input-field" name="fiscal_regime" value={formData.fiscal_regime} onChange={handleChange}>
                                    <option value="">Seleccione régimen fiscal...</option>
                                    {TAX_REGIMES.map(regime => (
                                        <option key={regime.code} value={regime.code}>{regime.code} - {regime.name}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="input-group span-full">
                                <label className="input-label">Dirección Fiscal</label>
                                <input className="input-field" name="fiscal_direccion" value={formData.fiscal_direccion || ''} onChange={handleChange} placeholder="Calle, número, colonia, CP, ciudad (si es diferente al domicilio)" />
                            </div>
                            <div className="input-group span-full">
                                <label className="input-label">Uso de CFDI (Factura)</label>
                                <select className="input-field" name="fiscal_cfdi_use" value={formData.fiscal_cfdi_use} onChange={handleChange}>
                                    <option value="">Seleccione uso de CFDI...</option>
                                    {CFDI_USES.map(use => (
                                        <option key={use.code} value={use.code}>{use.code} - {use.name}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>
                </form>

                {/* Footer Fijo */}
                <div className="form-footer">
                    <button type="button" onClick={onCancel} className="btn-cancel">
                        Cancelar
                    </button>
                    <button type="button" onClick={handleSubmit} className="btn-save" disabled={loading}>
                        {loading ? 'Guardando...' : 'Guardar Paciente'} <Save size={18} />
                    </button>
                </div>
            </div>
        </div>
    );
};

export default PatientForm;
